import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Mail, MessageCircle, Loader2 } from "lucide-react";
import { CONTACT, SERVICE_OPTIONS, QUANTITY_OPTIONS } from "../lib/data";
import { trackContact, trackLead } from "../lib/fb";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-black rounded-none px-4 py-3 font-mono text-sm placeholder:text-black/40 focus:outline-none focus:ring-0 focus:border-[#FF3B30] transition-colors duration-200";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-black/60 block mb-2">{label}</span>
    {children}
  </label>
);

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service_type: "", quantity: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_type || !form.quantity) {
      toast.error("Please select a service type and quantity.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      trackLead();
      toast.success("Enquiry received. We'll get back to you within 24 hours.");
      setForm({ name: "", phone: "", email: "", service_type: "", quantity: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-b border-black bg-[#F4F4F0]">
      <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-black/60 mb-4">( Work Order )</p>
          <h2 className="font-display font-bold uppercase tracking-tight leading-none text-4xl sm:text-6xl mb-10">
            Start a<br />print <span className="text-[#FF3B30]">run</span>
          </h2>

          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-link"
            onClick={trackContact}
            className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-widest bg-[#25D366] text-black border border-black px-6 py-4 mb-10 hover:bg-black hover:text-[#25D366] transition-colors duration-200"
          >
            <MessageCircle size={16} />
            Chat on WhatsApp — {CONTACT.whatsapp}
          </a>

          <div className="space-y-6 font-mono text-sm">
            <a href={CONTACT.mapsHref} target="_blank" rel="noopener noreferrer" data-testid="contact-address-link" className="flex gap-4 items-start hover:text-[#FF3B30] transition-colors duration-200">
              <MapPin size={18} className="shrink-0 mt-0.5 text-[#FF3B30]" />
              <span>{CONTACT.address}</span>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" onClick={trackContact} className="flex gap-4 items-center hover:text-[#FF3B30] transition-colors duration-200">
              <Mail size={18} className="shrink-0 text-[#FF3B30]" />
              <span>{CONTACT.email}</span>
            </a>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 border border-black p-5 sm:p-6 md:p-10 bg-[#EAEAEA]"
          data-testid="lead-form"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest flex justify-between border-b border-black pb-3 mb-8">
            <span>ENQUIRY FORM / SO-{new Date().getFullYear()}</span>
            <span className="text-[#FF3B30]">● REQUIRED</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Full name ●">
              <input required data-testid="lead-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} />
            </Field>
            <Field label="Phone ●">
              <input required data-testid="lead-phone-input" className={inputCls} placeholder="+91 ..." value={form.phone} onChange={set("phone")} />
            </Field>
            <Field label="Email ●">
              <input required type="email" data-testid="lead-email-input" className={inputCls} placeholder="you@company.com" value={form.email} onChange={set("email")} />
            </Field>
            <Field label="Service type ●">
              <select required data-testid="lead-service-select" className={inputCls} value={form.service_type} onChange={set("service_type")}>
                <option value="" disabled>Select service</option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Quantity ●">
              <select required data-testid="lead-quantity-select" className={inputCls} value={form.quantity} onChange={set("quantity")}>
                <option value="" disabled>Select quantity</option>
                {QUANTITY_OPTIONS.map((q) => (
                  <option key={q} value={q}>{q}</option>
                ))}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Job details">
                <textarea rows={4} data-testid="lead-message-input" className={inputCls} placeholder="Paper stock, sizes, finishing, deadline..." value={form.message} onChange={set("message")} />
              </Field>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="lead-submit-btn"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.25em] bg-black text-[#F4F4F0] py-5 hover:bg-[#FF3B30] transition-colors duration-200 disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : null}
            {loading ? "Sending..." : "Submit enquiry →"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
